import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import clientApi from '~/clientApi';
import { FEATURE_FLAGS, VIEWS_OPTIONS_MAPPED, VIEW_OPTIONS } from '~/constants';
import { generatePath } from '~/core/router';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { VIEW_TYPE, ViewType } from '~/screens/MyAgency/hooks/useActiveView';
import { selectTalentManager } from '~/selectors/talent-manager';

export type ViewOptionsMappedType = 'list' | 'stack';

export const viewTypeToViewOptionsMapper = {
  [VIEW_TYPE.stacked]: VIEW_OPTIONS.stacked,
  [VIEW_TYPE.list]: VIEW_OPTIONS.list,
  [VIEW_TYPE.card]: VIEW_OPTIONS.card,
};

interface IOptions {
  view: VIEW_OPTIONS;
  audienceFiltersHidden: boolean;
}

interface IMappedOptions {
  view: VIEWS_OPTIONS_MAPPED;
  hide_audience_filters: boolean;
}

interface PostCreateSharedLinkRequestBody {
  sharedLink?: string;
  onLinkClick?: Function;
  listId?: string;
  isEmailRequired: boolean;
  options: IOptions;
}

export const mapOptionsToApi = ({
  view,
  audienceFiltersHidden,
}: IOptions): IMappedOptions => {
  return {
    view: view === VIEW_OPTIONS.stacked ? VIEWS_OPTIONS_MAPPED.stack : view,
    hide_audience_filters: audienceFiltersHidden,
  } as IMappedOptions;
};

export const mapViewFromApi = (view: ViewOptionsMappedType): ViewType => {
  return view === VIEWS_OPTIONS_MAPPED.stack ? VIEW_OPTIONS.stacked : view;
};

export const useCreateSharedLink = () => {
  const manager = useSelector(selectTalentManager);
  const foamSharedActivityFF = useFeatureFlag(FEATURE_FLAGS.foamSharedActivity);
  const owners = useMemo(() => {
    const {
      id: managerId,
      agency: { id: agencyId },
    } = manager;
    return [
      { uuid: managerId, type: 'manager' },
      { uuid: agencyId, type: 'agency' },
    ];
  }, [manager]);

  return useMutation({
    mutationFn: async ({
      sharedLink = '',
      onLinkClick,
      listId,
      isEmailRequired,
      options,
    }: PostCreateSharedLinkRequestBody) => {
      const { audienceFiltersHidden, view } = options;
      const enabledControls = !audienceFiltersHidden
        ? ['audience-filters']
        : [];

      let link = sharedLink;

      if (foamSharedActivityFF === 'enabled' && listId) {
        return clientApi.savedLists
          .createSharedLink({
            entityId: listId,
            isEmailRequired,
            owners,
            options: mapOptionsToApi(options),
          })
          .then((response) => response['share-link'].url);
      }

      if (onLinkClick) {
        link = await onLinkClick(enabledControls);
      }

      return generatePath(link, {
        query: {
          view,
        },
      });
    },
  });
};
